import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin, Instagram } from 'lucide-react';
import { studioInfo, certifications } from '../mock';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const footerLinks = [
    { name: 'Chi Sono', path: '/about' },
    { name: 'Servizi', path: '/services' },
    { name: 'Portfolio', path: '/portfolio' },
    { name: 'Tecnologie', path: '/technologies' },
    { name: 'Contatti', path: '/contact' }
  ];

  return (
    <footer className="bg-[#0A1628] border-t border-white/5">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
          {/* Brand */}
          <div className="space-y-4">
            <div className="flex items-center space-x-3">
              <div className="relative w-11 h-11 flex-shrink-0">
                <div className="w-full h-full bg-transparent border-2 border-[#D4AF37] rounded-full flex items-center justify-center">
                  <span className="text-[#D4AF37] font-bold text-lg tracking-tighter" style={{fontFamily: 'Playfair Display, Georgia, serif'}}>GP</span>
                </div>
              </div>
              <div className="border-l border-white/10 pl-3">
                <p className="text-white font-medium text-base leading-tight" style={{fontFamily: 'Playfair Display, Georgia, serif'}}>Gabriele Pastore</p>
                <p className="text-gray-400 text-[10px] tracking-[0.25em] uppercase mt-1 font-light">Studio Tecnico</p>
              </div>
            </div>
            <p className="text-gray-400 text-sm leading-relaxed">
              {studioInfo.tagline}
            </p>
            <div className="flex space-x-3">
              <motion.a
                whileHover={{ scale: 1.1, y: -2 }}
                href="https://www.instagram.com/geom.gabrielepastore?igsh=cWcxMDI3aGtnN3l0&utm_source=qr"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                data-testid="footer-instagram-link"
                className="w-10 h-10 bg-white/5 hover:bg-gradient-to-br hover:from-[#E1306C] hover:via-[#F77737] hover:to-[#FCAF45] rounded-lg flex items-center justify-center transition-all duration-300 border border-white/10 hover:border-transparent"
              >
                <Instagram className="w-5 h-5 text-white" />
              </motion.a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-white font-semibold text-lg mb-4">Link Rapidi</h3>
            <ul className="space-y-2">
              {footerLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-gray-400 hover:text-[#D4AF37] text-sm transition-colors duration-300 inline-block hover:translate-x-1"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-white font-semibold text-lg mb-4">Contatti</h3>
            <ul className="space-y-3">
              <li>
                <a
                  href={`mailto:${studioInfo.email}`}
                  className="flex items-start space-x-3 text-gray-400 hover:text-[#D4AF37] text-sm transition-colors duration-300 group"
                >
                  <Mail className="w-5 h-5 mt-0.5 flex-shrink-0" />
                  <span className="break-all">{studioInfo.email}</span>
                </a>
              </li>
              <li>
                <a
                  href={`tel:${studioInfo.phone}`}
                  className="flex items-start space-x-3 text-gray-400 hover:text-[#D4AF37] text-sm transition-colors duration-300 group"
                >
                  <Phone className="w-5 h-5 mt-0.5 flex-shrink-0" />
                  <span>{studioInfo.phone}</span>
                </a>
              </li>
              <li>
                <div className="flex items-start space-x-3 text-gray-400 text-sm">
                  <MapPin className="w-5 h-5 mt-0.5 flex-shrink-0" />
                  <span>{studioInfo.address}</span>
                </div>
              </li>
            </ul>
          </div>

          {/* Certifications */}
          <div>
            <h3 className="text-white font-semibold text-lg mb-4">Abilitazioni</h3>
            <ul className="space-y-2">
              <li className="text-gray-400 text-sm">
                P.IVA: {studioInfo.piva}
              </li>
              <li className="text-gray-400 text-sm">
                Timbro: {studioInfo.timbro}
              </li>
              <li className="text-gray-400 text-sm">
                CSE/CSP D.Lgs. 81/08
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-white/5">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-gray-400 text-sm">
              © {currentYear} {studioInfo.name}. Tutti i diritti riservati.
            </p>
            <div className="flex space-x-6">
              <Link
                to="/privacy"
                className="text-gray-400 hover:text-[#D4AF37] text-sm transition-colors duration-300"
              >
                Privacy Policy
              </Link>
              <Link
                to="/terms"
                className="text-gray-400 hover:text-[#D4AF37] text-sm transition-colors duration-300"
              >
                Termini di Servizio
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
